
// client/src/pages/Kiosk.js
import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { getProducts, createManualOrder } from '../services/apiServices';
import styles from './Kiosk.module.css';
import logo from '../assets/images/logo.png';

const Kiosk = () => {
  const [products, setProducts] = useState({});
  const [cart, setCart] = useState([]);
  const [view, setView] = useState('welcome'); // 'welcome', 'categories', 'products', 'cart', 'checkout'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState(null);
  const [quantityModal, setQuantityModal] = useState(null); // Novo estado para o modal de quantidade
  const [orderSuccess, setOrderSuccess] = useState(null); // Novo estado para o modal de sucesso

  // 1. Conexão Real-time e Carga Inicial
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
    
    // CONFIGURAÇÃO ROBUSTA (Igual ao Dashboard/App.js)
    const newSocket = io(backendUrl, { 
      path: '/api/socket.io',
      transports: ['websocket', 'polling'] // Garante conexão em qualquer ambiente
    });

    // Função de carga definida dentro do efeito para garantir o escopo correto
    const loadProducts = async () => {
      try {
        const data = await getProducts();
        setProducts(data);
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
      }
    };

    // Carga inicial
    loadProducts();

    // Listener para atualização em tempo real (Dinâmica "Pull" do Dashboard)
    newSocket.on('stockUpdated', () => {
      console.log('[Totem] Estoque atualizado via Socket');
      loadProducts();
    });

    return () => newSocket.close();
  }, []);

  // 2. Lógica do Carrinho
  const addToCart = (productName, price, quantity = 1) => {
    setCart(prev => {
      const existing = prev.find(item => item.product === productName);
      if (existing) {
        return prev.map(item => 
          item.product === productName ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [...prev, { product: productName, price, quantity }];
    });
    setLastAddedItem({ name: productName });
  };

  const removeFromCart = (productName) => {
    setCart(prev => prev.filter(item => item.product !== productName));
  };

  const getTotal = () => cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  // 3. Finalizar Pedido
  const handleFinishOrder = async () => {
    if (!customerName.trim()) return alert('Por favor, digite seu nome.');
    setLoading(true);

    const orderData = {
      customerName: `${customerName} (Totem)`,
      customerId: `totem_${Date.now()}`, // ID único para o totem
      cart,
      deliveryType: 'pickup', // Define como Retirada para aparecer na coluna correta
      paymentMethod: 'Pagar no Caixa', // Padrão para totem
      deliveryFee: 0,
      paymentConfirmed: false,
      isPreOrder: false
    };

    try {
      const newOrder = await createManualOrder(orderData);
      setOrderSuccess({ customerName, orderNumber: newOrder.orderNumber }); // Abre o modal de sucesso
      setCart([]);
      setCustomerName('');
    } catch (error) {
      alert('Erro ao enviar pedido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setView('products');
  };

  const handleContinueShopping = () => {
    setLastAddedItem(null);
  };

  const handleGoToCart = () => {
    setLastAddedItem(null);
    setView('cart');
  };

  const handleCloseSuccess = () => {
    setOrderSuccess(null);
    setView('welcome');
  };

  // 4. Renderização (Simplificada para o exemplo)
  return (
    <div className={styles.container}>
      {/* Modal de Sucesso do Pedido */}
      {orderSuccess && (
        <div className={styles.successOverlay}>
          <div className={styles.successModalContent}>
            <div className={styles.successIcon}>🎉</div>
            <h2 className={styles.successTitle}>Sucesso!</h2>
            <p className={styles.successText}>
              Obrigado, <strong>{orderSuccess.customerName}</strong>!
            </p>
            
            <div className={styles.passwordBox}>
              <p className={styles.passwordLabel}>Sua Senha</p>
              <p className={styles.passwordNumber}>#{orderSuccess.orderNumber}</p>
            </div>

            <p className={styles.waitText}>
              Aguarde chamarmos sua senha no painel.
            </p>

            <button onClick={handleCloseSuccess} className={styles.closeButton}>
              OK, Fechar
            </button>
          </div>
        </div>
      )}

      {/* Modal de Seleção de Quantidade */}
      {quantityModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>{quantityModal.product}</h2>
            <p className={styles.modalPrice}>
              R$ {quantityModal.price.toFixed(2)} / un
            </p>
            
            <div className={styles.quantityControls}>
              <button 
                onClick={() => setQuantityModal(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))} 
                className={styles.quantityButton}
              >
                -
              </button>
              <span className={styles.quantityDisplay}>{quantityModal.quantity}</span>
              <button 
                onClick={() => setQuantityModal(prev => ({ ...prev, quantity: prev.quantity + 1 }))} 
                className={styles.quantityButton}
              >
                +
              </button>
            </div>

            <p className={styles.modalTotal}>
              Total: R$ {(quantityModal.price * quantityModal.quantity).toFixed(2)}
            </p>

            <div className={styles.modalActions}>
              <button 
                onClick={() => setQuantityModal(null)} 
                className={styles.cancelButton}
              >
                Cancelar
              </button>
              <button 
                onClick={() => { addToCart(quantityModal.product, quantityModal.price, quantityModal.quantity); setQuantityModal(null); }} 
                className={styles.confirmButton}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Adição */}
      {lastAddedItem && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.checkIcon}>✅</div>
            <h2 className={styles.addedTitle}>Adicionado!</h2>
            <p className={styles.addedText}>
              <strong>{lastAddedItem.name}</strong> foi para o carrinho.
            </p>
            
            <div className={styles.cartSummaryBox}>
              <h4 className={styles.cartSummaryTitle}>Seu Carrinho:</h4>
              {cart.map((item, index) => (
                <div key={index} className={styles.cartSummaryItem}>
                  <span>{item.quantity}x {item.product}</span>
                  <span>R$ {(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className={styles.cartSummaryTotal}>
                <span>Total:</span>
                <span>R$ {getTotal().toFixed(2)}</span>
              </div>
            </div>

            <div className={styles.modalButtonsColumn}>
              <button 
                onClick={handleContinueShopping}
                className={styles.continueButton}
              >
                Continuar Comprando
              </button>
              <button 
                onClick={handleGoToCart}
                className={styles.finishButtonModal}
              >
                Finalizar Pedido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      {view !== 'welcome' && (
        <div className={styles.header}>
          <img src={logo} alt="Bom Jesus" className={styles.headerLogo} />
          {cart.length > 0 && (
          <button 
            onClick={() => setView('cart')}
            className={styles.headerCartButton}
          >
            Ver Carrinho (R$ {getTotal().toFixed(2)})
          </button>
          )}
        </div>
      )}

      {/* View: WELCOME */}
      {view === 'welcome' && (
        <div className={styles.welcomeContainer}>
          <img src={logo} alt="Bom Jesus" className={styles.welcomeLogo} />
          <p className={styles.welcomeSubtitle}>Toque abaixo para iniciar seu pedido</p>
          <button 
            onClick={() => setView('categories')}
            className={styles.welcomeButton}
          >
            VER CARDÁPIO
          </button>
        </div>
      )}

      {/* View: CATEGORIES */}
      {view === 'categories' && (
        <div className={styles.categoriesContainer}>
          <h2 className={styles.categoriesTitle}>O que você deseja hoje?</h2>
          <div className={styles.categoriesGrid}>
            {[
              { id: 'assados', label: '🍖 Carnes Assadas', color: '#ff8a65' },
              { id: 'acompanhamentos', label: '🥗 Acompanhamentos', color: '#aed581' },
              { id: 'bebidas', label: '🥤 Bebidas', color: '#4fc3f7' }
            ].map(cat => (
              <button
                key={cat.id}
                onClick={() => handleCategorySelect(cat.id)}
                className={styles.categoryButton}
                style={{ border: `2px solid ${cat.color}` }}
              >
                <span className={styles.categoryIcon}>{cat.label.split(' ')[0]}</span>
                <span className={styles.categoryLabel}>{cat.label.split(' ').slice(1).join(' ')}</span>
              </button>
            ))}
          </div>
          <button onClick={() => setView('welcome')} className={styles.backButton}>
            &larr; Voltar ao Início
          </button>
        </div>
      )}

      {/* View: PRODUCTS */}
      {view === 'products' && (
        <div>
          <div className={styles.productsHeader}>
            <button onClick={() => setView('categories')} className={styles.backButtonSmall}>
              &larr; Voltar
            </button>
            <h2 className={styles.productsTitle}>{selectedCategory}</h2>
          </div>

        <div className={styles.productsGrid}>
          {Object.entries(products)
            .filter(([_, details]) => details.category === selectedCategory && details.stock > 0) // Filtra por categoria e estoque
            .map(([name, details]) => (
            <div key={name} className={styles.productCard}>
              <h3 className={styles.productName}>{name}</h3>
              <p className={styles.productPrice}>R$ {details.price.toFixed(2)}</p>
              <p className={styles.productStock}>Restam: {details.stock}</p>
              <button 
                onClick={() => setQuantityModal({ product: name, price: details.price, quantity: 1 })}
                className={styles.addButton}
              >
                Adicionar +
              </button>
            </div>
          ))}
        </div>
        {Object.entries(products).filter(([_, details]) => details.category === selectedCategory && details.stock > 0).length === 0 && (
          <p className={styles.emptyMessage}>Nenhum item disponível nesta categoria no momento.</p>
        )}
        </div>
      )}

      {/* View: CARRINHO / CHECKOUT */}
      {(view === 'cart' || view === 'checkout') && (
        <div className={styles.checkoutContainer}>
          <h2 className={styles.checkoutTitle}>Seu Pedido</h2>
          {cart.map(item => (
            <div key={item.product} className={styles.checkoutItemRow}>
              <span>{item.quantity}x {item.product}</span>
              <span>R$ {(item.price * item.quantity).toFixed(2)} <button onClick={() => removeFromCart(item.product)} className={styles.removeButton}>X</button></span>
            </div>
          ))}
          <h3 className={styles.checkoutTotal}>Total: R$ {getTotal().toFixed(2)}</h3>
          
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Seu Nome:</label>
            <input 
              type="text" 
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Digite seu nome para ser chamado"
              className={styles.inputField}
            />
          </div>

          <div className={styles.checkoutActions}>
            <button onClick={() => setView('categories')} className={styles.checkoutBackButton}>Voltar</button>
            <button onClick={handleFinishOrder} disabled={loading} className={styles.checkoutConfirmButton}>
              {loading ? 'Enviando...' : 'Confirmar Pedido'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Kiosk;
